import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import remarkGfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';
import type { ClaimGenerationReviewData, ClaimRepairEntry, ContractData } from './claimIntakeUtils';
import type { ClaimIntakeSession } from './claimIntakeAgentApi';
import { getClaimScoreAgentsStreamBaseUrl, runClaimScoreDebugPipeline } from './claimScoreAgentApi';
import {
  buildEnrichedClaimScoreFormState,
  claimObjectHasPartAndLaborLines,
  stableStringifyForScoreKey,
} from './claimScoreFormState';
import { CtySpinner } from '@circuitry-ai/doc-ui';
import { CtyButton } from '../button/CtyButton';
import { ClaimIntakeClaimScoreV2Panel } from '../viewer/ClaimIntakeClaimScoreV2Panel';
import { ClaimScoreGeneratorRenderer } from '../viewer/ClaimScoreGeneratorRenderer';
import { FullScreenModal } from '../viewer/FullScreenModal';
import { getActionOutputV2Payload, type ActionOutputV2Payload } from '../viewer/actionOutputV2Utils';
import { ScoreThresholdsProvider, type ScoreThresholds } from '../viewer/claimScoreRendererShared';
import { useAgentScoreThresholds } from '../viewer/useAgentScoreThresholds';

const GAUGE_R = 54;
const GAUGE_C = 2 * Math.PI * GAUGE_R;

const MARKDOWN_STUBS = {
  ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc pl-4 space-y-1 my-2">{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal pl-4 space-y-1 my-2">{children}</ol>,
  p: ({ children }: { children?: React.ReactNode }) => <p className="my-1.5">{children}</p>,
} as const;

function ClaimScoreFullViewButton(props: Readonly<{ onClick: () => void; className?: string }>): ReactElement {
  const { onClick, className = '' } = props;
  return (
    <button
      type="button"
      onClick={onClick}
      title="View Full Screen"
      aria-label="View Full Screen"
      className={`p-1.5 hover:bg-default-200 rounded-full shrink-0 ${className}`}
    >
      <Icon icon="mdi:fullscreen" className="text-default-500 w-5 h-5" />
    </button>
  );
}

function buildClaimScoreRendererData(
  scoreActionOutput: ActionOutputV2Payload,
  contract: ContractData | null | undefined,
  liveScore: number | null,
): Record<string, unknown>[] {
  const entityNumber = contract?.contractNumber ?? contract?.vehicle?.vin ?? 'Unknown';
  return [{
    agent_name: 'Claim Score',
    action_output: scoreActionOutput,
    entity_number: entityNumber,
    agent_score: liveScore ?? scoreActionOutput.score,
  }];
}

function ClaimScoreFullViewModal(props: Readonly<{
  isOpen: boolean;
  onClose: () => void;
  claimData: Record<string, unknown>[] | null;
}>): ReactElement | null {
  const { isOpen, onClose, claimData } = props;
  if (!claimData) return null;
  return (
    <FullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      agentName="Claim Score"
      modalClassName="claim-score-generator"
      headerBorder
    >
      <ClaimScoreGeneratorRenderer
        claimData={claimData}
        showActionButtons={false}
        isModal
        source="agent_history"
      />
    </FullScreenModal>
  );
}

function ClaimScoreReasonList(props: Readonly<{ lines: string[]; compact: boolean; title: string }>): ReactElement | null {
  const { lines, compact, title } = props;
  if (lines.length === 0) return null;
  const textCls = compact ? 'text-[11px]' : 'text-[12px]';
  return (
    <div className={`w-full min-w-0 ${compact ? 'mt-1' : 'mt-3'}`}>
      <p className={`${textCls} font-semibold text-default-700 mb-1.5`}>{title}</p>
      <ul className={`${textCls} text-default-500 space-y-1.5 list-disc pl-4 leading-snug ${compact ? 'max-h-36' : 'max-h-44'} overflow-y-auto`}>
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

function gaugeStroke(liveScore: number | null, isScoring: boolean, thresholds: ScoreThresholds): string {
  if (isScoring) return '#94a3b8';
  if (liveScore == null) return '#e5e7eb';
  if (liveScore >= thresholds.greenMin) return '#22c55e';
  if (liveScore >= thresholds.yellowMin) return '#eab308';
  return '#ef4444';
}

function ClaimScoreCircularGauge(props: Readonly<{ size: 'compact' | 'desktop'; liveScore: number | null; isScoring: boolean; thresholds: ScoreThresholds }>): ReactElement {
  const { size, liveScore, isScoring, thresholds } = props;
  const progress = liveScore == null ? 0 : Math.min(100, Math.max(0, liveScore)) / 100;
  const dashOffset = GAUGE_C * (1 - progress);
  const strokeColor = gaugeStroke(liveScore, isScoring, thresholds);
  const svgClass = size === 'compact' ? 'w-16 h-16' : 'w-28 h-28';
  const strokeW = size === 'compact' ? 8 : 6;
  const centerScoreClass = size === 'compact' ? 'text-lg font-bold' : 'text-3xl font-bold';
  const denomClass = size === 'compact' ? 'text-[9px]' : 'text-[11px]';

  let center: string;
  if (isScoring) center = '…';
  else if (liveScore == null) center = '—';
  else center = String(liveScore);

  return (
    <div className={`relative shrink-0 ${svgClass}`}>
      <svg className={`${svgClass} -rotate-90`} viewBox="0 0 128 128">
        <circle className="cty-gauge-track" cx="64" cy="64" r={GAUGE_R} fill="none" stroke="#e8ecf0" strokeWidth={strokeW} />
        <circle
          cx="64"
          cy="64"
          r={GAUGE_R}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeW}
          strokeLinecap="round"
          strokeDasharray={GAUGE_C}
          strokeDashoffset={dashOffset}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${centerScoreClass} text-foreground`}>{center}</span>
        <span className={`${denomClass} text-default-400`}>/ 100</span>
      </div>
    </div>
  );
}
function buildThresholdLegend(thresholds: ScoreThresholds): { swatch: string; range: string }[] {
  const green = Math.round(thresholds.greenMin);
  const yellow = Math.round(thresholds.yellowMin);
  return [
    { swatch: 'bg-green-500', range: `${green}–100` },
    { swatch: 'bg-yellow-500', range: `${yellow}–${green - 1}` },
    { swatch: 'bg-red-500', range: `0–${yellow - 1}` },
  ];
}

function extractReasonLines(outputsSnapshot: unknown): string[] {
  if (outputsSnapshot == null || typeof outputsSnapshot !== 'object' || Array.isArray(outputsSnapshot)) {
    return [];
  }
  const snap = outputsSnapshot as Record<string, unknown>;
  const raw = snap['reasonLines'] ?? snap['reason_lines'];
  return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
}

type ScoreStateSetters = {
  setLiveScore: (v: number | null) => void;
  setScoreReasonLines: (v: string[]) => void;
  setScoreExplanationMd: (v: string) => void;
  setScoreActionOutput: (v: ActionOutputV2Payload | null) => void;
  onDummyClaimIdChange?: (id: string | null) => void;
};

function computeTierLabel(liveScore: number | null, thresholds: ScoreThresholds): { text: string; style: string } {
  if (liveScore == null) return { text: 'NOT STARTED', style: 'text-default-500 border-default-300' };
  if (liveScore >= thresholds.greenMin) return { text: 'Strong', style: 'text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/40 bg-green-50 dark:bg-green-500/10' };
  if (liveScore >= thresholds.yellowMin) return { text: 'Review Required', style: 'text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10' };
  return { text: 'At Risk', style: 'text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10' };
}

function applyWorkflowToScoreState(
  wf: NonNullable<{ scoreDisplay?: number | null; explanationMarkdown: string; outputsSnapshot?: unknown }>,
  setters: ScoreStateSetters,
): void {
  if (wf.scoreDisplay != null) setters.setLiveScore(wf.scoreDisplay);
  setters.setScoreReasonLines(extractReasonLines(wf.outputsSnapshot));
  if (wf.explanationMarkdown.trim()) setters.setScoreExplanationMd(wf.explanationMarkdown.trim());
  const snap = wf.outputsSnapshot as Record<string, unknown> | null | undefined;
  setters.setScoreActionOutput(getActionOutputV2Payload(snap));
  const nextDummyClaimId = typeof snap?.dummy_claim_id === 'string' ? snap.dummy_claim_id : null;
  if (nextDummyClaimId) setters.onDummyClaimIdChange?.(nextDummyClaimId);
}

export type ClaimScoreSidebarProps = Readonly<{
  currentStep: number;
  completedSteps: number[];
  compact?: boolean;
  authToken?: string | null;
  contract?: ContractData | null;
  claimGenerationReview?: ClaimGenerationReviewData | null;
  /** From Repairs & Docs when claim has part + labor lines; merged with 3Cs. */
  claimScoreEnrichedForm?: Record<string, unknown> | null;
  step3Repairs?: ClaimRepairEntry[];
  dummyClaimId?: string | null;
  onDummyClaimIdChange?: (id: string | null) => void;
  /** Existing claim-intake session; the score event reuses its aiworkerId + sessionId. */
  session?: ClaimIntakeSession | null;
  /** Shared usage log id from estimateparser / VIN — sent on score dify_stream for unified logging. */
  aiworkerUsageId?: string | null;
  /**
   * Claim intake mounts two score sidebars (mobile compact + lg right rail); both stay in the DOM.
   * Only the instance matching the active breakpoint should run the pipeline.
   */
  runClaimScorePipeline?: boolean;
  /** Notifies parent when the active sidebar instance is running the score pipeline. */
  onClaimScoreLoadingChange?: (loading: boolean) => void;
  /** Uploaded estimate PDFs — shown on steps after contract lookup. */
  pdfFiles?: readonly File[];
  onViewPdf?: (file?: File) => void;
}>;

export function ClaimScoreSidebar({
  currentStep,
  completedSteps,
  compact,
  authToken,
  contract,
  claimGenerationReview,
  claimScoreEnrichedForm,
  step3Repairs,
  dummyClaimId,
  onDummyClaimIdChange,
  session,
  aiworkerUsageId,
  runClaimScorePipeline = true,
  onClaimScoreLoadingChange,
  pdfFiles = [],
  onViewPdf,
}: ClaimScoreSidebarProps) {
  const { t } = useTranslation();
  const hasPdfFiles = pdfFiles.length > 0;
  const { thresholds: scoreThresholds, isReady: areScoreThresholdsReady } = useAgentScoreThresholds(session?.aiworkerId);
  const thresholdLegend = useMemo(() => buildThresholdLegend(scoreThresholds), [scoreThresholds]);
  const [liveScore, setLiveScore] = useState<number | null>(null);
  const [scoreExplanationMd, setScoreExplanationMd] = useState('');
  const [scoreReasonLines, setScoreReasonLines] = useState<string[]>([]);
  const [scoreActionOutput, setScoreActionOutput] = useState<ActionOutputV2Payload | null>(null);
  const [isScoring, setIsScoring] = useState(false);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  /** Skips duplicate score API calls when enriched payload is unchanged (e.g. navigating back from Review). */
  const lastScoredFormKeyRef = useRef<string | null>(null);

  const derivedEnrichedForm = useMemo((): Record<string, unknown> | null => {
    if (claimScoreEnrichedForm) return claimScoreEnrichedForm;
    const co = claimGenerationReview?.claimObject;
    if (co && claimObjectHasPartAndLaborLines(co)) {
      return buildEnrichedClaimScoreFormState(co, step3Repairs ?? []);
    }
    return null;
  }, [claimGenerationReview?.claimObject, claimScoreEnrichedForm, step3Repairs]);

  useEffect(() => {
    lastScoredFormKeyRef.current = null;
    if (!contract) {
      setLiveScore(null);
      setScoreExplanationMd('');
      setScoreReasonLines([]);
      setScoreActionOutput(null);
      setIsScoring(false);
    }
  }, [contract?.contractNumber, contract?.vehicle?.vin]);

  const tierLabel = useMemo(() => computeTierLabel(liveScore, scoreThresholds), [liveScore, scoreThresholds]);
  const claimScoreClaimData = useMemo(
    () => (scoreActionOutput ? buildClaimScoreRendererData(scoreActionOutput, contract, liveScore) : null),
    [scoreActionOutput, contract, liveScore],
  );

  /**
   * Score only after 3csmapper has produced part/labor lines, then on each real form-state change.
   * Skips contract/VIN-only triggers and duplicate runs when the user returns with the same payload.
   */
  useEffect(() => {
    const token = authToken?.trim();
    if (!runClaimScorePipeline || !token || !contract || !session) return undefined;
    if (!derivedEnrichedForm || !claimObjectHasPartAndLaborLines(derivedEnrichedForm)) return undefined;

    const formKey = stableStringifyForScoreKey({
      contractNumber: contract.contractNumber,
      vin: contract.vehicle?.vin,
      formState: derivedEnrichedForm,
      ...(aiworkerUsageId ? { aiworkerUsageId } : {}),
    });
    if (formKey === lastScoredFormKeyRef.current) return undefined;

    const formState = derivedEnrichedForm;
    const delayMs = 450;
    const baseUrl = getClaimScoreAgentsStreamBaseUrl();
    const controller = new AbortController();
    const setters: ScoreStateSetters = {
      setLiveScore,
      setScoreReasonLines,
      setScoreExplanationMd,
      setScoreActionOutput,
      onDummyClaimIdChange,
    };

    const invoke = async (): Promise<void> => {
      setIsScoring(true);
      try {
        const res = await runClaimScoreDebugPipeline({
          token,
          baseUrl,
          session,
          formState,
          contractsData: [contract],
          dummyClaimId,
          aiworkerUsageId: aiworkerUsageId ?? undefined,
          signal: controller.signal,
        });
        if (controller.signal.aborted || res.skipped) return;
        lastScoredFormKeyRef.current = formKey;
        const wf = res.dify.workflowFinished;
        if (wf) applyWorkflowToScoreState(wf, setters);
      } catch (err: unknown) {
        if ((err as Error)?.name === 'AbortError') return;
        // eslint-disable-next-line no-console -- claim score pipeline
        console.error('[ClaimScore] pipeline error:', err);
      } finally {
        setIsScoring(false);
      }
    };

    const timerId = globalThis.setTimeout(() => { void invoke(); }, delayMs);
    return () => {
      globalThis.clearTimeout(timerId);
      controller.abort();
    };
  }, [runClaimScorePipeline, authToken, contract, session, derivedEnrichedForm, dummyClaimId, aiworkerUsageId, onDummyClaimIdChange]);

  useEffect(() => {
    if (!runClaimScorePipeline || !onClaimScoreLoadingChange) return undefined;
    onClaimScoreLoadingChange(isScoring);
    return () => {
      onClaimScoreLoadingChange(false);
    };
  }, [isScoring, runClaimScorePipeline, onClaimScoreLoadingChange]);

  const showEmptyHint = !scoreExplanationMd && scoreReasonLines.length === 0 && !scoreActionOutput;

  if (!areScoreThresholdsReady) {
    return (
      <div className="flex items-center justify-center p-6">
        <CtySpinner size="sm" />
      </div>
    );
  }

  if (compact) {
    return (
      <ScoreThresholdsProvider value={scoreThresholds}>
        <div className="bg-content1 rounded-xl border border-default-200 p-4 flex flex-col gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <ClaimScoreCircularGauge size="compact" liveScore={liveScore} isScoring={isScoring} thresholds={scoreThresholds} />
            <div className="flex flex-col gap-1 min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <h3 className="text-sm font-semibold text-foreground">Claim Score</h3>
                {claimScoreClaimData ? (
                  <ClaimScoreFullViewButton onClick={() => setIsFullScreenOpen(true)} />
                ) : null}
              </div>
              <span
              className={`text-[10px] font-semibold tracking-wider rounded-full px-2.5 py-0.5 uppercase w-fit border ${
                isScoring ? 'text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/10' : tierLabel.style
              }`}
            >
              {isScoring ? 'updating the score...' : tierLabel.text}
            </span>
            {isScoring && <CtySpinner size="sm" />}
            {showEmptyHint && (
              <p className="text-[11px] text-default-400 italic">Score updates as you complete each step</p>
            )}
            </div>
          </div>
          <ClaimScoreReasonList compact lines={scoreReasonLines} title="Reasons" />
        {hasPdfFiles && currentStep > 0 ? (
          <CtyButton
            variant="bordered"
            size="sm"
            className="font-semibold w-full"
            startContent={<Icon icon="mdi:file-pdf-box" width={16} />}
            onPress={() => onViewPdf?.()}
          >
            {pdfFiles.length > 1 ? t('View PDFs ({{count}})', { count: pdfFiles.length }) : t('View PDF')}
          </CtyButton>
        ) : null}
        {scoreExplanationMd ? (
          <div className="text-[11px] text-default-500 max-h-32 overflow-y-auto border-t border-default-200 pt-2">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_STUBS}>
              {scoreExplanationMd}
            </ReactMarkdown>
          </div>
        ) : null}
        {scoreActionOutput ? (
          <div className="max-h-72 overflow-y-auto overflow-x-hidden -mx-1">
            <ClaimIntakeClaimScoreV2Panel payload={scoreActionOutput} />
          </div>
        ) : null}
        </div>
        <ClaimScoreFullViewModal
          isOpen={isFullScreenOpen}
          onClose={() => setIsFullScreenOpen(false)}
          claimData={claimScoreClaimData}
        />
      </ScoreThresholdsProvider>
    );
  }

  return (
    <ScoreThresholdsProvider value={scoreThresholds}>
    <div className="bg-content1 border-l border-default-200 w-full min-w-0 flex flex-col h-full min-h-0 overflow-hidden shrink-0">
      <div className="px-5 pt-5 pb-0 shrink-0 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-foreground">Claim Score</h3>
        {claimScoreClaimData ? (
          <ClaimScoreFullViewButton onClick={() => setIsFullScreenOpen(true)} />
        ) : null}
      </div>

      <div className="border-b border-default-200 mt-3" />

      <div className="relative flex flex-col items-center w-full pt-2 pb-2 px-3 shrink-0">
        <ClaimScoreCircularGauge size="desktop" liveScore={liveScore} isScoring={isScoring} thresholds={scoreThresholds} />
        <div className="absolute right-3 top-2 flex flex-col gap-2">
          {thresholdLegend.map((row) => (
            <div key={row.range} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-sm ${row.swatch}`} />
              <span className="text-[12px] text-default-500">{row.range}</span>
            </div>
          ))}
        </div>

        <div className="mt-3 mb-1 flex flex-row items-center gap-2">
          <span
            className={`text-[11px] font-semibold tracking-wider rounded-full px-3 py-1 uppercase border ${
              isScoring ? 'text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/10' : tierLabel.style
            }`}
          >
            {isScoring ? 'UPDATING THE SCORE' : tierLabel.text}
          </span>
           {isScoring && <CtySpinner size="sm" /> }
        </div>
        <ClaimScoreReasonList compact={false} lines={scoreReasonLines} title="Reasons" />
      </div>

      {hasPdfFiles && currentStep > 0 ? (
        <div className="px-5 pb-3">
          <CtyButton
            variant="bordered"
            size="sm"
            className="font-semibold w-full"
            startContent={<Icon icon="mdi:file-pdf-box" width={16} />}
            onPress={() => onViewPdf?.()}
          >
            {pdfFiles.length > 1 ? t('View PDFs ({{count}})', { count: pdfFiles.length }) : t('View PDF')}
          </CtyButton>
        </div>
      ) : null}

      {scoreExplanationMd ? (
        <div className="text-[12px] text-default-500 text-left px-5 mt-2 mb-4 max-h-48 overflow-y-auto leading-snug">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_STUBS}>
            {scoreExplanationMd}
          </ReactMarkdown>
        </div>
      ) : null}
      {showEmptyHint && (
        <p className="text-[12px] text-default-400 italic text-center px-3 mt-1 mb-1">
          Score updates as you complete each step
        </p>
      )}

      {scoreActionOutput ? (
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-3">
          <ClaimIntakeClaimScoreV2Panel payload={scoreActionOutput} />
        </div>
      ) : (
        <div className="flex-1 min-h-0" />
      )}

    </div>
    <ClaimScoreFullViewModal
      isOpen={isFullScreenOpen}
      onClose={() => setIsFullScreenOpen(false)}
      claimData={claimScoreClaimData}
    />
    </ScoreThresholdsProvider>
  );
}
